import { ChangeEvent } from 'react';
import '../WaterfallAnalysis.scss';
import { NSCellInput, NSDropdown } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { numeralFormatter } from 'ns_libs/formatter';
import { ProFormaGenericColumn } from 'views/ProFormaTable/components/ProFormaGenericTable/types';
import { IContributionsCashFlow, IGpGpContributionsCashFlow } from 'views/ProFormaTable/types';
import { Periods } from 'components/ProFormaReturnsTable/constant';

export const getContributionsTotals = (
    contributionsCashFlow: (IContributionsCashFlow | IGpGpContributionsCashFlow)[] = [],
    cashFlowPeriodsByCadence?: Record<Periods, string[]>,
    periodCadence?: Periods,
    maxIndex?: number,
) => {
    const totals: Record<string, string | undefined> = {};
    const allPeriods = periodCadence && cashFlowPeriodsByCadence ? cashFlowPeriodsByCadence[periodCadence] : [];
    const periods = maxIndex !== undefined ? allPeriods.slice(0, maxIndex + 1) : allPeriods;

    periods.forEach((_, index) => {
        const periodTotal = contributionsCashFlow[index]?.totalAmount;
        totals[`column${index + 1}`] = periodTotal;
    });

    return totals;
};

export const CONTRIBUTIONS_COLUMNS: ProFormaGenericColumn[] = [
    {
        key: 'name', field: 'name', header: 'Contributions', format: 'none', showLabel: true, type: 'text',
    },
    {
        key: 'upTo', field: 'upTo', header: '', format: 'none', isHidden: true,
    },
    {
        key: 'empty', field: 'empty', header: '', format: 'none', isHidden: true,
    },
    {
        key: 'share', field: 'share', header: 'Share', format: 'percentage',
    },
    {
        key: 'amount', field: 'amount', header: 'Amount', format: 'currency', isReadOnly: true,
    },
];

export const getWaterfallColumns: (
    selectedIRR: IOptionAnyValue,
    setSelectedIRR: (val: IOptionAnyValue) => void,
    showGpCatchUp: boolean,
    onUpdatePrefCatchUp?: (value: number) => void
) => ProFormaGenericColumn[] = (
    selectedIRR,
    setSelectedIRR,
    showGpCatchUp,
    onUpdatePrefCatchUp,
) => [
    {
        key: 'name',
        field: 'name',
        header: 'Distribution waterfall',
        format: 'none',
        isReadOnly: true,
        customCell: {
            shouldShowCustomCell: () => true,
            renderCell: ({ row }) => {
                const isPref = row.id === '0';
                const options = [
                    { label: '100%', value: 1.0 },
                    { label: '50% / 50%', value: 0.5 },
                    { label: 'None', value: 0.0 },
                ];
                const raw = (row as any)?.gpCatchUp;
                const asNumber = raw == null ? 0.0 : Number(typeof raw === 'string' ? parseFloat(raw) : raw);
                const selected = options.find(o => Number(o.value) === asNumber) ?? options[2];

                return (
                    <div className="d-flex justify-content-between align-items-start w-100">
                        <span className="text-white my-auto">{String(row.name ?? '')}</span>

                        {showGpCatchUp && isPref && (
                            <div className="ml-3 d-flex flex-column align-items-start">
                                <div style={{ width: 220 }}>
                                    <div className="text-white small mt-1 ml-2">GP catch-up</div>
                                    <NSDropdown
                                        options={options}
                                        selectedOption={selected}
                                        placeholder="None"
                                        handleSelectOption={opt => onUpdatePrefCatchUp?.(Number(opt.value))}
                                        toggleClassName="w-100 px-2 py-1"
                                        badgeColor="dark-lighten"
                                        isBadgeToggle
                                        useBodyContainer
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                );
            },
        },
    },
    {
        key: 'upTo',
        field: 'upTo',
        header: 'IRR',
        format: 'percentage',
        headerDropdown: {
            options: [
                { value: 'irr', label: 'IRR' },
                { value: 'equity_multiple', label: 'EMx' },
            ],
            value: selectedIRR,
            onChange: setSelectedIRR,
            placeholder: 'Select',
            label: 'hurdle',
            width: '100px',
        },
        isReadOnly: true,
        customCell: {
            shouldShowCustomCell: () => true,
            renderCell: ({ onChange, row, value }) => (
                <div className="d-flex justify-content-end">
                    <div
                        className="WaterfallAnalysis--hurdle-label text-muted text-right mr-1 my-auto"
                    >
                        {row.label}
                    </div>
                    {row.id !== '3' ? (
                        <div className="WaterfallAnalysis--hurdle-input">
                            <NSCellInput
                                type="number"
                                numberType="percentage"
                                value={value || 0}
                                handleUpdateValue={updatedValue => onChange({ target: { value: updatedValue } } as ChangeEvent<HTMLInputElement>)}
                                placeholder="Enter percentage"
                                isRightAligned
                                inputHeight="26px"
                                inputWidth="70px"
                            />
                        </div>
                    ) : (
                        <span className="WaterfallAnalysis--hurdle-input text-white">
                            {typeof value === 'number' && value ? `${numeralFormatter(value * 100, '0,0.00')}%` : '-'}
                        </span>
                    )}
                </div>
            ),
        },
    },
    {
        key: 'gpPromote', field: 'gpPromote', header: 'GP promote', format: 'percentage', isReadOnly: row => row.id === '0',
    },
    {
        key: 'distToGP', field: 'distToGP', header: 'Dist. % to GP', format: 'percentage', isReadOnly: row => row.id !== 'pref-return',
    },
    {
        key: 'distToLP', field: 'distToLP', header: 'Dist. % to LP', format: 'percentage', isReadOnly: row => row.id !== 'pref-return',
    },
];

export const DISTRIBUTIONS_COLUMNS: ProFormaGenericColumn[] = [
    {
        key: 'name', field: 'name', header: 'Distributions', format: 'none', showLabel: true,
    },
    {
        key: 'upTo', field: 'upTo', header: '', format: 'none', isHidden: true,
    },
    {
        key: 'empty', field: 'empty', header: '', format: 'none', isHidden: true,
    },
    {
        key: 'share', field: 'share', header: 'Share', format: 'percentage',
    },
    {
        key: 'amount', field: 'amount', header: 'Amount', format: 'currency',
    },
];

export const CASH_FLOW_COLUMNS: ProFormaGenericColumn[] = [
    {
        key: 'name', field: 'name', header: 'Net cash flow', format: 'none', showLabel: true,
    },
    {
        key: 'emx', field: 'emx', header: 'EMx', format: 'ratio', isNegative: false,
    },
    {
        key: 'irr', field: 'irr', header: 'IRR', format: 'percentage', isNegative: false,
    },
    {
        key: 'share', field: 'share', header: 'Share', format: 'percentage', isNegative: false,
    },
    {
        key: 'amount', field: 'amount', header: 'Amount', format: 'currency', isNegative: false,
    },
];
